/* oxlint-disable import/max-dependencies -- session orchestrator aggregates the cohesive pty submodules (queue, tailer, hook socket, spawn args, settings, coalescer, tool server) */
/**
 * PtySession — orchestrates one `claude` CLI run under a pseudo-terminal.
 *
 * It merges two event sources into a single stream:
 *   1. the on-disk transcript JSONL (tailed via JsonlTailer), and
 *   2. PostToolUse progress envelopes relayed over a Unix socket.
 *
 * Raw PTY stdout is never turned into HarnessEvents — only the trusted
 * transcript file and the validated socket envelopes become events. When a
 * PtyBridge is supplied, that raw stdout is instead mirrored verbatim to the
 * S2 relay so the S5 "Connected TUI" terminal can render it, and relayed
 * keystrokes / resizes are fed back into the pty.
 *
 * `node-pty` is loaded lazily so that SDK-only consumers never pull in the
 * native module.
 */

import { mkdtemp, mkdir, rm, stat } from "node:fs/promises";
import { tmpdir } from "node:os";
import { join, dirname } from "node:path";
import type {
  HarnessEvent,
  HarnessQueryOptions,
  HarnessUserMessage,
  HarnessUserQuestion,
  PtyBridge,
} from "../types.js";
import { AsyncEventQueue } from "./event-queue.js";
import {
  HookSocketServer,
  type ToolProgress,
  type PreToolUseRequest,
  type PreToolUseVerdict,
} from "./hook-socket.js";
import { JsonlTailer } from "./jsonl-tailer.js";
import { buildSpawnArgs, resolveClaudeBinary, buildExitErrors } from "./spawn-args.js";
import { writeHookSettings, sessionTranscriptPath } from "./settings.js";
import { PtyOutputCoalescer } from "./output-coalescer.js";
import { PtyToolServer, startToolServers } from "./tool-server.js";

// Cap on the rolling tail of raw terminal output retained for diagnostics when
// `claude` exits without a result. Bounded so a long-running session can't grow
// this unboundedly; only the most recent bytes (where a failure surfaces) matter.
const MAX_DIAGNOSTIC_OUTPUT = 4000;

interface PtyProcess {
  onData(listener: (data: string) => void): void;
  onExit(listener: (event: { exitCode: number }) => void): void;
  write(data: string): void;
  resize(cols: number, rows: number): void;
  kill(signal?: string): void;
}

interface PtySpawnOptions {
  name: string;
  cols: number;
  rows: number;
  cwd: string;
  env: Record<string, string>;
}

type PtySpawn = (file: string, args: string[], options: PtySpawnOptions) => PtyProcess;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function extractSpawn(mod: unknown): PtySpawn | null {
  if (!isRecord(mod)) return null;
  if (typeof mod.spawn === "function") return mod.spawn as PtySpawn;
  const def = mod.default;
  if (isRecord(def) && typeof def.spawn === "function") return def.spawn as PtySpawn;
  return null;
}

async function loadPtySpawn(): Promise<PtySpawn> {
  const mod: unknown = await import("node-pty");
  const spawn = extractSpawn(mod);
  if (!spawn) throw new Error("node-pty: spawn export not found");
  return spawn;
}

export function inheritedEnv(socketPath: string): Record<string, string> {
  const env: Record<string, string> = {};
  for (const [key, value] of Object.entries(process.env)) {
    if (typeof value === "string") env[key] = value;
  }
  // Do NOT force CLAUDE_CONFIG_DIR. Forcing it to ~/.claude makes the CLI read
  // <dir>/.claude.json — a fresh, un-onboarded config — instead of the user's
  // real ~/.claude.json, which re-triggers the first-run theme picker + login
  // even though valid credentials already exist in ~/.claude/. The loop above
  // already propagates an explicitly-set CLAUDE_CONFIG_DIR (pods/codespaces),
  // and the config dir is unchanged either way, so transcript tailing (which
  // uses claudeConfigHome()) still resolves to the same projects/ directory.
  env.CONVEYOR_HOOK_SOCKET = socketPath;
  return env;
}

/**
 * The bracketed-paste bytes written to the CLI's stdin to deliver a prompt.
 * The text is wrapped in paste markers so embedded newlines land as literal
 * lines in the input box instead of submitting it early. The submitting Enter
 * is deliberately NOT part of these bytes: deliverPrompt() sends it as a
 * separate write after a settle window, because an Enter folded into the same
 * chunk as the paste can be swallowed by the CLI's paste-burst handling —
 * observed in production as an auto-mode prompt parked unsubmitted in the
 * input box. "prefill" delivery never sends an Enter at all.
 */
export function buildPromptBytes(text: string): string {
  return `\x1b[200~${text}\x1b[201~`;
}

/** Settle window between the paste write and the submitting Enter write. */
const SUBMIT_SETTLE_MS = 300;
// Re-press cadence/bounds for the submit nudge (see armSubmitNudge). Fast
// presses cover the Enter-swallowed-at-startup race; the slow phase covers
// startup dialogs that render tens of seconds in on a cold pod (folder
// trust, onboarding) — observed live 2026-07-02 parking code reviews ~13min+
// when the trust dialog mounted after the fast window had expired.
const SUBMIT_NUDGE_INTERVAL_MS = 2000;
const SUBMIT_NUDGE_MAX_PRESSES = 5;
const SUBMIT_NUDGE_SLOW_INTERVAL_MS = 5000;
const SUBMIT_NUDGE_WINDOW_MS = 90_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Current size of the transcript, or 0 if it does not exist yet. */
async function transcriptSize(path: string): Promise<number> {
  try {
    return (await stat(path)).size;
  } catch {
    return 0;
  }
}

/**
 * Defensive extraction of AskUserQuestion's `questions` input from the hook
 * payload. Malformed entries are dropped rather than thrown — the event is
 * observe-only, so an empty list still arms the waiting_for_input report.
 */
export function parseUserQuestions(input: Record<string, unknown>): HarnessUserQuestion[] {
  if (!Array.isArray(input.questions)) return [];
  const questions: HarnessUserQuestion[] = [];
  for (const entry of input.questions) {
    if (!isRecord(entry)) continue;
    if (typeof entry.question !== "string") continue;
    const options = Array.isArray(entry.options)
      ? entry.options
          .filter(isRecord)
          .filter((o) => typeof o.label === "string")
          .map((o) => ({
            label: o.label as string,
            description: typeof o.description === "string" ? o.description : "",
          }))
      : [];
    questions.push({
      question: entry.question,
      header: typeof entry.header === "string" ? entry.header : "",
      options,
      ...(typeof entry.multiSelect === "boolean" ? { multiSelect: entry.multiSelect } : {}),
    });
  }
  return questions;
}

export class PtySession {
  private readonly queue = new AsyncEventQueue<HarnessEvent>();
  private socket: HookSocketServer | null = null;
  private tailer: JsonlTailer | null = null;
  private pty: PtyProcess | null = null;
  private tempDir = "";
  private sawResult = false;
  // Rolling tail of raw PTY output, retained only to enrich the error when the
  // CLI exits before emitting a result (the bytes are otherwise relayed to S5
  // and never become events). Trimmed to MAX_DIAGNOSTIC_OUTPUT on every write.
  private recentOutput = "";
  private coalescer: PtyOutputCoalescer | null = null;
  private _toreDown = false;
  private readonly exitListeners: ((code: number) => void)[] = [];
  private readonly idleListeners: (() => void)[] = [];
  private abortHandler: (() => void) | null = null;
  private cols = 120;
  private rows = 40;
  private unsubInput: (() => void) | null = null;
  private unsubResize: (() => void) | null = null;
  // In-process HTTP MCP servers exposing the harness tools to the spawned CLI,
  // plus the path to the `--mcp-config` that points at them.
  private toolServers: PtyToolServer[] = [];
  private mcpConfigPath: string | null = null;
  // Plan-dialog auto-accept state (see armPlanDialogAutoAccept).
  private pendingPlanApproval = false;
  private planApprovalTimer: NodeJS.Timeout | null = null;
  // Submit-nudge state (see armSubmitNudge).
  private pendingSubmitNudge = false;
  private submitNudgeTimer: NodeJS.Timeout | null = null;

  constructor(
    private readonly prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>,
    private readonly options: HarnessQueryOptions,
    private readonly resume?: string,
    private readonly bridge?: PtyBridge,
  ) {}

  onIdle(listener: () => void): void {
    this.idleListeners.push(listener);
  }

  onExit(listener: (code: number) => void): void {
    this.exitListeners.push(listener);
  }

  get isToreDown(): boolean {
    return this._toreDown;
  }

  get hookSocketPath(): string | null {
    return this.socket ? this.socket.socketPath : null;
  }

  events(): AsyncGenerator<HarnessEvent, void> {
    return this.queue.drain();
  }

  async start(): Promise<void> {
    // Fail fast before allocating resources: the tailer can only locate the
    // transcript if we know which session id the CLI will write to.
    const sessionId = this.resume ?? this.options.sessionId;
    if (!sessionId) {
      throw new Error("PtySession requires options.sessionId or a resume target");
    }
    // stop()/softStop() abort the query's shared AbortController. The SDK
    // harness consumes it natively; under the PTY harness we must translate an
    // abort into teardown() — otherwise a mid-turn `claude` (which may never
    // emit a transcript `result` and does not exit) is left running while the
    // event consumer stays parked on the queue.
    const signal = this.options.abortController?.signal;
    if (signal?.aborted) {
      // Already aborted before we allocated anything — tear down (which closes
      // the empty queue so a parked consumer completes) and bail.
      await this.teardown();
      return;
    }
    try {
      this.tempDir = await mkdtemp(join(tmpdir(), "conveyor-pty-"));
      const socketPath = join(this.tempDir, "hook.sock");
      this.socket = new HookSocketServer(
        socketPath,
        (progress) => this.handleProgress(progress),
        (request) => this.handlePreToolUse(request),
      );
      await this.socket.listen();
      const { settingsPath } = await writeHookSettings(this.tempDir);
      // Expose the harness's in-process tools to the spawned CLI over a loopback
      // HTTP MCP server, written into a `--mcp-config` consumed by spawn().
      await this.setupToolServers();
      const transcriptPath = sessionTranscriptPath(this.options.cwd, sessionId);
      await mkdir(dirname(transcriptPath), { recursive: true });
      // On resume the transcript already holds the prior session's records; start
      // tailing at EOF so historical records aren't replayed as fresh events.
      const startOffset = this.resume ? await transcriptSize(transcriptPath) : 0;
      this.tailer = new JsonlTailer(transcriptPath, (event) => this.handleTranscriptEvent(event));
      this.tailer.start(startOffset);
      await this.spawn(settingsPath, socketPath);
      // Register after spawn so the handler has a live pty to kill, then
      // re-check: an abort racing with allocation (fired before the listener
      // attached) would otherwise be missed.
      if (signal) {
        this.abortHandler = () => {
          void this.teardown();
        };
        signal.addEventListener("abort", this.abortHandler, { once: true });
        if (signal.aborted) {
          await this.teardown();
          return;
        }
      }
      // Wire relayed keystrokes / resizes into the live pty. Registered only
      // after a successful spawn so writeStdin/resizePty have a process to
      // target; the unsubscribers are cleared in teardown().
      if (this.bridge) {
        this.unsubInput = this.bridge.onInput((data) => this.writeStdin(data));
        this.unsubResize = this.bridge.onResize((cols, rows) => this.resizePty(cols, rows));
      }
      await this.feedPrompt();
    } catch (err) {
      // A failure after we begin allocating (socket listen, tailer interval,
      // temp dir, spawn) would otherwise leak those resources: the PtyHarness
      // consumer only calls teardown() once start() has resolved.
      await this.teardown();
      throw err;
    }
  }

  writeStdin(text: string): void {
    this.pty?.write(text);
  }

  /** Apply a relayed resize to the live pty (reconciled dims from the server). */
  private resizePty(cols: number, rows: number): void {
    if (cols <= 0 || rows <= 0) return;
    this.cols = cols;
    this.rows = rows;
    try {
      this.pty?.resize(cols, rows);
    } catch {
      /* pty already exited */
    }
  }

  /**
   * Force the CLI to repaint its full screen by wiggling the pty size
   * (SIGWINCH). Used after a socket reconnect: the server's scrollback ring is
   * per-process and starts empty on a new instance, so without a repaint a
   * quiet TUI would leave the Connected-TUI terminal blank (and hidden) until
   * the CLI next draws on its own.
   */
  forceRepaint(): void {
    if (!this.pty) return;
    try {
      this.pty.resize(this.cols, Math.max(1, this.rows - 1));
      this.pty.resize(this.cols, this.rows);
    } catch {
      /* pty already exited */
    }
  }

  async teardown(): Promise<void> {
    if (this._toreDown) return;
    this._toreDown = true;
    this.disarmPlanDialogAutoAccept();
    this.disarmSubmitNudge();
    this.unsubInput?.();
    this.unsubInput = null;
    this.unsubResize?.();
    this.unsubResize = null;
    if (this.abortHandler) {
      this.options.abortController?.signal.removeEventListener("abort", this.abortHandler);
      this.abortHandler = null;
    }
    try {
      this.pty?.kill();
    } catch {
      /* pty already exited */
    }
    this.pty = null;
    this.coalescer?.dispose();
    this.coalescer = null;
    this.tailer?.close();
    this.tailer = null;
    if (this.socket) {
      await this.socket.close();
      this.socket = null;
    }
    for (const toolServer of this.toolServers) {
      try {
        await toolServer.close();
      } catch {
        /* already closed */
      }
    }
    this.toolServers = [];
    this.mcpConfigPath = null;
    this.queue.close();
    if (this.tempDir) {
      await rm(this.tempDir, { recursive: true, force: true });
      this.tempDir = "";
    }
  }

  /**
   * Serve the harness's in-process tools to the spawned CLI over loopback HTTP
   * (handlers run in THIS process against the live task-token connection) and
   * record the `--mcp-config` path for spawn(). No-op when the harness was
   * constructed without tools (e.g. SDK-only callers).
   */
  private async setupToolServers(): Promise<void> {
    const { servers, mcpConfigPath } = await startToolServers(
      this.options.mcpServers ?? {},
      this.tempDir,
    );
    this.toolServers = servers;
    this.mcpConfigPath = mcpConfigPath;
  }

  private async spawn(settingsPath: string, socketPath: string): Promise<void> {
    const args = buildSpawnArgs({
      resume: this.resume,
      sessionId: this.options.sessionId,
      model: this.options.model,
      permissionMode: this.options.permissionMode,
      settingsPath,
      ...(this.options.appendSystemPrompt
        ? { appendSystemPrompt: this.options.appendSystemPrompt }
        : {}),
      // Only this config: ignore the user's ~/.claude.json / project .mcp.json
      // so the agent's tool set is deterministic (and a stale personal conveyor
      // server doesn't load).
      ...(this.mcpConfigPath ? { mcpConfigPath: this.mcpConfigPath, strictMcpConfig: true } : {}),
    });
    const spawn = await loadPtySpawn();
    const pty = spawn(resolveClaudeBinary(), args, {
      name: "xterm-color",
      cols: this.cols,
      rows: this.rows,
      cwd: this.options.cwd,
      env: inheritedEnv(socketPath),
    });
    // Mirror raw terminal output to the relay (and on to the S5 terminal). The
    // first flush creates the server-side scrollback ring — which is what makes
    // the Connected-TUI terminal appear, i.e. this doubles as the attach
    // handshake. Output is coalesced (~40ms windows) so a busy redraw becomes
    // one relay call instead of tens — per-frame server cost was starving the
    // stream. Stdout is intentionally NOT turned into HarnessEvents.
    const bridge = this.bridge;
    if (bridge) {
      this.coalescer = new PtyOutputCoalescer(
        (data, dims) => {
          bridge.sendOutput(data, dims);
        },
        () => ({ cols: this.cols, rows: this.rows }),
      );
    }
    pty.onData((data) => {
      this.coalescer?.write(data);
      // Retain a bounded tail so finalizeOnExit can explain an early exit.
      this.recentOutput = (this.recentOutput + data).slice(-MAX_DIAGNOSTIC_OUTPUT);
    });
    pty.onExit((event) => {
      void this.finalizeOnExit(event.exitCode);
    });
    this.pty = pty;
  }

  private async deliverPrompt(text: string): Promise<void> {
    this.writeStdin(buildPromptBytes(text));
    if (this.options.promptDelivery === "prefill") return;
    // The Enter goes in a separate write after a settle window — folded into
    // the same chunk as the paste, the CLI can treat it as part of the paste
    // burst and leave the prompt parked unsubmitted in the input box.
    await sleep(SUBMIT_SETTLE_MS);
    if (this._toreDown) return;
    this.writeStdin("\r");
    this.armSubmitNudge();
  }

  private async feedPrompt(): Promise<void> {
    if (typeof this.prompt === "string") {
      await this.deliverPrompt(this.prompt);
      return;
    }
    for await (const message of this.prompt) {
      const content = message.message.content;
      const text = typeof content === "string" ? content : JSON.stringify(content);
      await this.deliverPrompt(text);
    }
  }

  /**
   * Even as a separate write, the submitting Enter can race CLI startup and
   * be swallowed, leaving the pasted prompt parked in the input box with
   * nothing to un-park it (the 45s parked-TUI watchdog only reports status).
   * Until the first transcript record proves the turn started, re-press Enter
   * on a bounded interval. Enter on an empty or mid-turn input box is a no-op.
   * Deliberate trade-off (same as the plan-dialog auto-accept below): a press
   * can accept the default of an unexpected startup dialog — in a headless
   * pod that unblocks the run rather than parking it forever.
   *
   * Two phases: SUBMIT_NUDGE_MAX_PRESSES fast presses for the swallowed-Enter
   * race, then a slow cadence out to SUBMIT_NUDGE_WINDOW_MS for startup
   * dialogs that render late on cold pods (the folder-trust dialog's default
   * is the accept option, so one late Enter un-parks the run).
   */
  private armSubmitNudge(): void {
    if (this.submitNudgeTimer) return;
    this.pendingSubmitNudge = true;
    const startedAt = Date.now();
    let presses = 0;
    const press = (): void => {
      if (
        this._toreDown ||
        !this.pendingSubmitNudge ||
        Date.now() - startedAt >= SUBMIT_NUDGE_WINDOW_MS
      ) {
        this.disarmSubmitNudge();
        return;
      }
      presses++;
      this.writeStdin("\r");
      if (presses === SUBMIT_NUDGE_MAX_PRESSES && this.submitNudgeTimer) {
        clearInterval(this.submitNudgeTimer);
        this.submitNudgeTimer = setInterval(press, SUBMIT_NUDGE_SLOW_INTERVAL_MS);
      }
    };
    this.submitNudgeTimer = setInterval(press, SUBMIT_NUDGE_INTERVAL_MS);
  }

  private disarmSubmitNudge(): void {
    this.pendingSubmitNudge = false;
    if (this.submitNudgeTimer) {
      clearInterval(this.submitNudgeTimer);
      this.submitNudgeTimer = null;
    }
  }

  private handleProgress(progress: ToolProgress): void {
    this.queue.push({
      type: "tool_progress",
      ...(progress.tool_name === undefined ? {} : { tool_name: progress.tool_name }),
      ...(progress.elapsed_time_seconds === undefined
        ? {}
        : { elapsed_time_seconds: progress.elapsed_time_seconds }),
    });
  }

  /**
   * PreToolUse request from the hook helper → the runner's `canUseTool`
   * verdict. ExitPlanMode is the Conveyor plan-exit gate (validation +
   * identification trigger run inside the callback); AskUserQuestion is
   * observed and always allowed. Absent callback → allow (mirrors the SDK
   * default).
   */
  private async handlePreToolUse(request: PreToolUseRequest): Promise<PreToolUseVerdict> {
    if (request.tool_name === "AskUserQuestion") {
      // Observe-only: the TUI renders the questionnaire and collects the
      // answer natively. Never delegate to `canUseTool` — the SDK-era handler
      // there parks on a blocking RPC, which under PTY would time the hook
      // out and deny the questionnaire. Disarm the submit nudge first: a
      // pending Enter press would answer the dialog's default option.
      this.disarmSubmitNudge();
      this.queue.push({ type: "user_question", questions: parseUserQuestions(request.tool_input) });
      return { decision: "allow" };
    }
    const canUseTool = this.options.canUseTool;
    let verdict: PreToolUseVerdict;
    if (canUseTool) {
      const result = await canUseTool(request.tool_name, request.tool_input);
      verdict =
        result.behavior === "allow"
          ? { decision: "allow" }
          : { decision: "deny", reason: result.message };
    } else {
      verdict = { decision: "allow" };
    }
    if (
      verdict.decision === "allow" &&
      request.tool_name === "ExitPlanMode" &&
      this.options.planDialogAutoAccept
    ) {
      this.armPlanDialogAutoAccept();
    }
    return verdict;
  }

  /**
   * A hook "allow" does not bypass the CLI's plan-approval dialog — it renders
   * right after the verdict. Press Enter (default: "Yes, auto-accept edits")
   * until the turn resumes; the next transcript record only lands once the
   * dialog is answered, so it doubles as the stop signal.
   */
  private armPlanDialogAutoAccept(): void {
    if (this.planApprovalTimer) return;
    this.pendingPlanApproval = true;
    let presses = 0;
    const press = (): void => {
      if (this._toreDown || !this.pendingPlanApproval || presses >= 6) {
        this.disarmPlanDialogAutoAccept();
        return;
      }
      presses++;
      this.writeStdin("\r");
    };
    this.planApprovalTimer = setInterval(press, 1500);
    setTimeout(press, 700);
  }

  private disarmPlanDialogAutoAccept(): void {
    this.pendingPlanApproval = false;
    if (this.planApprovalTimer) {
      clearInterval(this.planApprovalTimer);
      this.planApprovalTimer = null;
    }
  }

  private handleTranscriptEvent(event: HarnessEvent): void {
    // Any new transcript record after an allowed ExitPlanMode means the plan
    // dialog was answered — stop pressing Enter.
    if (this.pendingPlanApproval) this.disarmPlanDialogAutoAccept();
    // Any new transcript record proves the submitted prompt started a turn —
    // the submit nudge has done its job.
    if (this.pendingSubmitNudge) this.disarmSubmitNudge();
    this.queue.push(event);
    if (event.type === "result") {
      this.sawResult = true;
      for (const listener of this.idleListeners) listener();
      // A transcript `result` marks end-of-query. Close the stream so the
      // generator completes — mirroring the SDK harness's per-query model.
      // Interactive `claude` does not exit after a result, so we must not wait
      // for process exit to terminate the event stream.
      this.queue.close();
    }
  }

  private async finalizeOnExit(exitCode: number): Promise<void> {
    // Ship any buffered terminal tail (exit message, final repaint) before the
    // relay goes quiet — runs even on the teardown path, where dispose() below
    // may already have flushed (flush is a no-op when empty).
    this.coalescer?.flush();
    // A teardown-driven kill (stop/softStop/abort) fires this exit handler, but
    // the turn is already discarded — pushing a synthetic error result here
    // would be spurious and would race teardown()'s queue.close().
    if (this._toreDown) return;
    if (this.tailer) {
      this.tailer.close();
      await this.tailer.flush();
    }
    if (!this.sawResult) {
      // Fold the swallowed terminal tail into the error so a bare exit (missing
      // `claude` binary, auth stop, unknown flag) is diagnosable from the logs
      // rather than only from the live S5 terminal.
      this.queue.push({
        type: "result",
        subtype: "error",
        errors: buildExitErrors(exitCode, this.recentOutput, resolveClaudeBinary()),
      });
    }
    for (const listener of this.exitListeners) listener(exitCode);
    this.queue.close();
  }
}
